/* @flow */
/* eslint-env es6,browser */

import {
  getPageUrl,
  isJSONSupported,
  isSecure,
  isDNT,
  BrowserUtil
} from '../utils/browserutil';
import bidbase from './bidbase';
import { isFetchSupported, fetchJSON } from '../utils/fetch';
import { logError } from '../utils/logger';
import { apiName, apiVersion, getRandom, generateUUID } from '../utils';
import { extend } from '../utils/coreutils';
import { allSettledPromise } from '../utils/promiseutils';
import { getRTBUserEids } from '../idproviders/idputils';

const ADAPTER_VERSION = '1.1.0';

/**
 * @module bidders/adtech
 * @requires module:logger
 * @requires module:coreutils
 * @requires module:promiseutils
 */
/**
 * @global
 * @typedef {object} adtechConfig
 * @description extends {@link hbConfig}
 * @property {string} bidKey - bid key
 * @property {string} id -bid id
 * @property {string} server - server
 */
/**
 * @global
 * @typedef hbPosData
 * @property {string} info Info to log
 * @property {object} _QP - bid value(cpm) are populated here
 * @property {object} _CV - partner specific pos id
 */
/**
 * @description Implements functionality for adtech header bidding
 * @augments _bidbase
 * @requires module:utils
 */

/**
 * typedef definitions
 */
/**
 * typedef definitions
 */
declare type slotData = {|
  id: string,
  secure: number,
  tagid: string,
  ext: {
    dfp_ad_unit_code: string
  },
  banner: {
    mimes: Array<string>,
    format: Array<{ w: number, h: number }>
  }
|};

declare type rtbUid = {|
  id: string,
  atype: number
|};
declare type rtbEid = {|
  source: string,
  uids: Array<rtbUid>
|};
declare type userInfo = {|
  user: {
    ext: {
      eids: Array<rtbEid>
    }
  }
|};
declare type siteInfo = {|
  site: {
    id?: string,
    page: string
  }
|};
declare type regsInfo = {|
  regs: {
    ext: {
      us_privacy: string
    }
  }
|};
declare type deviceInfo = {|
  device: {
    ua: string,
    language: string,
    dnt: number,
    h: number,
    w: number
  }
|};
declare type extInfo = {|
  ext: {
    ttx: {
      caller: [
        {
          name: string,
          version: string
        }
      ]
    }
  }
|};

declare type adtechReqBody = {|
  id: string,
  imp: Array<slotData>,
  ...siteInfo,
  ...userInfo,
  ...regsInfo,
  ...extInfo
|};

declare type adtechBidRequestCache = {
  impid: {
    [string]: string
  }
};

class _adtechbid extends bidbase {
  _bidRequest: adtechBidRequestCache;

  _config: adtechConfig;

  _DefConfig: adtechConfig;

  _bidResponses: { [key: string]: RTBBidResponse };

  constructor(name: string = 'adtech') {
    super(name);
    this._DefConfig = extend(true, {}, this._DefConfig, {
      id: '',
      pubID: '',
      sspDcn: '',
      bidUrl: ''
    });
    this._config = extend(true, {}, this._DefConfig);
    this._mandatoryConfigs = ['id', 'server'];
    this._isSupported = isJSONSupported() && isFetchSupported();
    this._bidRequest = {
      impid: {}
    };
  }

  _processBidResponse(
    posList: Array<string>,
    ret: hbPartnerData
  ): hbPartnerData {
    posList.forEach(pos => {
      const resp: RTBBidResponse = this._bidResponses[pos];
      if (!this._isValidRTBResponse(resp)) {
        return;
      }
      // eslint-disable-next-line prefer-destructuring
      const res = resp.seatbid[0].bid[0];
      if (res && res.adm) {
        const {
          adId = '',
          impid = '',
          id = '',
          price = 0,
          adm,
          nurl = ''
        } = res;
        const cpm = Number(res.ext && res.ext.encp ? res.ext.encp : price);
        this._handleBidPosData(
          ret,
          pos,
          `${cpm.toFixed(2)}`,
          impid || id || adId,
          adm,
          nurl
        );
      }
      delete this._bidResponses[pos];
    });
    return ret;
  }

  getEids(): anyObject {
    // as of now below IDs are supported
    // we will be sending "ext<key=<value>" in query params
    const { user: { eids = [] } = {} } = getRTBUserEids();
    // $FlowFixMe[infer-error]
    return Object.fromEntries(
      eids.map(({ source, uids: [{ id }] }) => [`eid${source}`, id])
    );
  }

  _getDevice(): deviceInfo {
    return {
      device: {
        ua: BrowserUtil.getUserAgent(),
        // eslint-disable-next-line compat/compat
        language: navigator.language || '',
        dnt: isDNT() ? 1 : 0,
        h: window.screen.height,
        w: window.screen.width
      }
    };
  }

  /**
   * Create slot object for an ad position
   *
   * @param {string} pos - Ad position
   * @returns {object} - Request URL info for given position
   * @access private
   */
  _getSlotData(pos: string): slotData {
    // get all the required params
    const randid = getRandom(20);
    const sizes = [...(this._config.posTokenMap[pos].sizes || [[728, 90]])];
    const fmt = sizes.map(([w, h]) => ({ w, h }));
    // const gpid = adconfig.getGPID(pos);
    const params = {
      id: randid,
      secure: isSecure() ? 1 : 0,
      banner: {
        mimes: [
          'text/html',
          'text/javascript',
          'application/javascript',
          'image/jpg'
        ],
        format: fmt
      },
      ext: {
        // eslint-disable-next-line camelcase
        dfp_ad_unit_code: pos
      },
      tagid: this._config.posTokenMap[pos].sspPos
    };
    this._bidRequest.impid[randid] = pos;
    return params;
  }

  _getBid(pos: string, commonData: adtechReqBody): Promise<boolean> {
    const url = this._config.bidUrl;
    const postData = extend({ id: generateUUID() }, commonData, {
      imp: [this._getSlotData(pos)]
    });
    const opt = {
      data: {},
      timeout: this._config.timeout,
      logPrefix: pos
    };
    const req = {
      headers: {
        'Content-Type': 'application/json',
        'x-openrtb-version': '2.5'
      },
      method: 'POST',
      body: JSON.stringify(postData),
      credentials: 'include'
    };
    return fetchJSON(url, opt, req).then((json: RTBBidResponse) => {
      this._bidResponses[pos] = json;
      return true;
    });
  }

  _buildCommonData(): adtechReqBody {
    const loc: Location = window.location;
    return extend(
      {
        cur: ['USD'],
        site: {
          page: getPageUrl(),
          publisher: {
            id: this._config.pubID
          },
          id: this._config.sspDcn,
          domain: loc.host
        },
        regs: { ext: this._getPrivacyString() },
        source: {
          ext: {
            hb: 1,
            adapterver: ADAPTER_VERSION,
            prebidver: '8.44.0',
            integration: {
              name: apiName(),
              ver: apiVersion()
            }
          },
          fd: 1
        }
      },
      this._getDevice(),
      getRTBUserEids()
    );
  }

  /**
   * get bids for given positions
   *
   * @param {Array<string>} posList - HB position list
   * @param {object.<string, hbPosData>} ret - return data
   * @returns {Promise<object.<string, hbPosData>>} - Promise
   * @access private
   */
  _getAds(posList: Array<string>, ret: hbPartnerData): Promise<hbPartnerData> {
    this._bidRequest.impid = {};
    this._bidResponses = {};

    const commonData = this._buildCommonData();
    // Create promise array with all bid requests
    const pArray = posList.map(pos => this._getBid(pos, commonData));
    return allSettledPromise(pArray).then(
      arr => {
        const fValues = arr
          .filter(v => v.state !== 'fulfilled')
          .map(v => v.reason);
        if (fValues.length) {
          // Log all failed promise errors
          logError(`${this._name} bidding errors: ${fValues.join(',')}`);
        }
        return this._processBidResponse(posList, ret);
      },
      error => {
        logError(`${this._name} bidding failed: ${error || ''}`);
        return ret;
      }
    );
  }
}

const adtechbid: _adtechbid = new _adtechbid();
export default adtechbid;
